import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import countries from '../../data/countries.json';
import REGIONS from '../../data/regions.json';

const countryIso2Entries = Object.entries(countries).map(([iso2, meta]) => ({
  iso2,
  bbox: meta.bbox,
  region: meta.region, // UN region slug stored in countries.json
  area: (meta.bbox.lat_max - meta.bbox.lat_min) * (meta.bbox.lon_max - meta.bbox.lon_min),
}));

// Find the most specific country whose bbox contains (lat, lon).
// NOTE: the simple `lon >= lon_min && lon <= lon_max` check mis-handles any
// bbox that crosses the 180° antimeridian (Russia, Fiji, Kiribati, US with
// Aleutians). countries.json in this repo normalises those ranges into a
// single rectangle that spans most of the globe, so they'll still "contain"
// the map centre — just imprecisely. Acceptable today because those countries
// aren't among the top-density merchants and the UI only uses this to suggest
// a region switch. Revisit if/when we ship country-level data for those.
function countryAt(lat, lon) {
  const containing = countryIso2Entries.filter(c => {
    const b = c.bbox;
    return lat >= b.lat_min && lat <= b.lat_max && lon >= b.lon_min && lon <= b.lon_max;
  });
  if (!containing.length) return null;
  // Smallest area wins (coastal overlaps resolve to the more specific country).
  containing.sort((a, b) => a.area - b.area);
  return containing[0];
}

/**
 * ShowRegionButton
 *
 * Renders a floating pill when the map centre has drifted into a different
 * UN region than the current page.  Clicking navigates to the region-wide map
 * (/map/region/<slug>) — giving users the broadest useful context rather than
 * another single-country silo.
 *
 * Props
 * -----
 * mapBounds        — Leaflet LatLngBounds (or bbox object) for the current view
 * currentIso2      — ISO2 of the country page (e.g. "AU") — present on /map/:iso2
 * currentRegionSlug — slug of the region page (e.g. "oceania") — present on /map/region/:slug
 */
export default function ShowRegionButton({ mapBounds, currentIso2 = null, currentRegionSlug = null }) {
  const navigate = useNavigate();

  const target = useMemo(() => {
    if (!mapBounds) return null;

    const center = mapBounds.getCenter
      ? mapBounds.getCenter()
      : {
          lat: (mapBounds.lat_min + mapBounds.lat_max) / 2,
          lng: (mapBounds.lon_min + mapBounds.lon_max) / 2,
        };

    const hit = countryAt(center.lat, center.lng);
    if (!hit || !hit.region) return null; // open ocean — hide button

    const targetSlug = hit.region; // UN region slug from countries.json

    // On a country page (/map/:iso2):
    //   - If map centre is still in the SAME country, no switch needed.
    //   - If map centre has drifted to ANY other country, offer the region-wide view.
    if (currentIso2) {
      if (hit.iso2.toUpperCase() === currentIso2.toUpperCase()) return null;
      return { slug: targetSlug, name: REGIONS[targetSlug]?.name || targetSlug };
    }

    // On a region page (/map/region/:slug):
    //   - If map centre is still within the same region, no switch needed.
    //   - If map centre has drifted to a DIFFERENT region, offer that region.
    if (currentRegionSlug) {
      if (currentRegionSlug === targetSlug) return null;
      return { slug: targetSlug, name: REGIONS[targetSlug]?.name || targetSlug };
    }

    return null;
  }, [mapBounds, currentIso2, currentRegionSlug]);

  if (!target) return null;

  return (
    <button
      type="button"
      onClick={() => navigate(`/map/region/${target.slug}`)}
      className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-3 py-1 text-[11px] font-medium shadow-md whitespace-nowrap transition-colors"
      data-testid="show-region-button"
    >
      <MapPin className="w-3 h-3" />
      Show merchants across {target.name} &rarr;
    </button>
  );
}
